import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { Card } from '../Card';
import { useChatPanel } from './useChatPanel';

type Props = {
  /** Pinned above the conversation, typically a `ListPanelHeader` with the conversation title. Sticky on phones. */
  header?: ReactNode;
  /** The conversation (`ChatMessage`s), or a welcome state before the first question. */
  children: ReactNode;
  /** The question form, pinned under the conversation. */
  composer: ReactNode;
  /** Changes when a message is added: brings the end of the conversation into view. */
  scrollKey?: unknown;
  /** Accessible name of the conversation log. */
  label?: string;
  /**
   * Fills the remaining height of a fixed-header page from the `sm` breakpoint and scrolls the
   * conversation inside the panel. On phones the page scrolls and the composer sticks to the bottom.
   */
  fill?: boolean;
  'data-testid'?: string;
};

type OwnerState = {
  fill: boolean;
};

const DEFAULT_LABEL = 'Conversation';
// Keeps the conversation at a comfortable reading measure on wide screens.
const MAX_CONVERSATION_WIDTH = 95;

const Root = styled(Card)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  // `Card` clips its content; the sticky composer needs the page as scroll container on phones.
  overflow: 'visible',
  ...(ownerState.fill && {
    [theme.breakpoints.up('sm')]: {
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
  }),
}));

// Height of the `AppShell` phone top bar, exposed by the shell as a CSS variable (0 outside a shell).
const SHELL_TOP_OFFSET = 'var(--app-shell-top-offset, 0px)';

const Header = styled('div')(({ theme }) => ({
  flexShrink: 0,
  // On phones the page scrolls: the title and its actions stay reachable under the top bar.
  [theme.breakpoints.down('sm')]: {
    position: 'sticky',
    top: SHELL_TOP_OFFSET,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
}));

const Log = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  ...(ownerState.fill && {
    [theme.breakpoints.up('sm')]: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      padding: theme.spacing(3),
    },
  }),
}));

const Conversation = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: theme.spacing(MAX_CONVERSATION_WIDTH),
  marginInline: 'auto',
  // Short conversations sit at the bottom, next to the composer, like any messaging app.
  marginTop: 'auto',
}));

const Composer = styled('div')(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
  flexShrink: 0,
  padding: theme.spacing(2),
  paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('sm')]: {
    paddingInline: theme.spacing(3),
  },
}));

const ComposerInner = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: theme.spacing(MAX_CONVERSATION_WIDTH),
  marginInline: 'auto',
}));

/**
 * Working surface of a conversation with the AI assistant: a scrolling log and a composer that
 * always stays reachable under it.
 */
export function ChatPanel(props: Props) {
  const hook = useChatPanel(props);
  const ownerState = { fill: Boolean(props.fill) };

  return (
    <Root ownerState={ownerState} data-testid={props['data-testid'] ?? 'chat-panel'}>
      {props.header ? <Header>{props.header}</Header> : null}
      <Log ownerState={ownerState} role="log" aria-live="polite" aria-label={props.label ?? DEFAULT_LABEL}>
        <Conversation>
          {props.children}
          <div ref={hook.endRef} />
        </Conversation>
      </Log>
      <Composer>
        <ComposerInner>{props.composer}</ComposerInner>
      </Composer>
    </Root>
  );
}

export type { Props as ChatPanelProps };
